const MIME_EXTENSION_MAP = {
  'image/gif': 'gif',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/svg+xml': 'svg',
  'image/webp': 'webp',
};

const DEFAULT_DOWNLOAD_DELAY_MS = 120;

export const isIOSFamilyDevice = (
  userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '',
  platform = typeof navigator !== 'undefined' ? navigator.platform : '',
  maxTouchPoints = typeof navigator !== 'undefined' ? navigator.maxTouchPoints : 0,
) => {
  const isClassicIOS = /iPad|iPhone|iPod/i.test(userAgent);
  const isModernIPadOS = platform === 'MacIntel' && Number(maxTouchPoints) > 1;

  return isClassicIOS || isModernIPadOS;
};

export const inferFileExtensionFromUrl = (fileUrl = '') => {
  const sanitizedUrl = String(fileUrl).split(/[?#]/, 1)[0];
  const match = sanitizedUrl.match(/\.([a-z0-9]+)$/i);

  return match?.[1]?.toLowerCase() || '';
};

export const inferFileExtensionFromMimeType = (mimeType = '') => {
  const normalizedMimeType = String(mimeType).split(';', 1)[0].trim().toLowerCase();
  return MIME_EXTENSION_MAP[normalizedMimeType] || '';
};

export const buildFilename = (filenameBase = 'download', extension = '') => {
  const safeBase = String(filenameBase).trim() || 'download';
  const normalizedExtension = String(extension).trim().replace(/^\./, '');

  if (!normalizedExtension) {
    return safeBase;
  }

  if (safeBase.toLowerCase().endsWith(`.${normalizedExtension.toLowerCase()}`)) {
    return safeBase;
  }

  return `${safeBase}.${normalizedExtension}`;
};

export const canvasToBlob = (canvas, type = 'image/png', quality) => new Promise((resolve, reject) => {
  if (!canvas) {
    reject(new Error('Canvas is required.'));
    return;
  }

  canvas.toBlob((blob) => {
    if (!blob) {
      reject(new Error('Failed to create blob from canvas.'));
      return;
    }

    resolve(blob);
  }, type, quality);
});

export const canvasToFile = async (canvas, filenameBase, options = {}) => {
  const { type = 'image/png', quality } = options;
  const blob = await canvasToBlob(canvas, type, quality);
  const extension = inferFileExtensionFromMimeType(blob.type) || inferFileExtensionFromMimeType(type);
  const filename = buildFilename(filenameBase, extension);

  return new File([blob], filename, {
    type: blob.type || type,
    lastModified: Date.now(),
  });
};

export const fetchUrlAsFile = async (fileUrl, filenameBase = 'download') => {
  const response = await fetch(fileUrl, { credentials: 'include' });

  if (!response.ok) {
    throw new Error(`Failed to fetch file: HTTP ${response.status}`);
  }

  const blob = await response.blob();
  const extension = inferFileExtensionFromUrl(fileUrl) || inferFileExtensionFromMimeType(blob.type);
  const filename = buildFilename(filenameBase, extension);

  return new File([blob], filename, {
    type: blob.type || 'application/octet-stream',
    lastModified: Date.now(),
  });
};

export const downloadFile = (file) => {
  const objectUrl = URL.createObjectURL(file);
  const anchor = document.createElement('a');

  anchor.href = objectUrl;
  anchor.download = file.name || 'download';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();

  URL.revokeObjectURL(objectUrl);
};

export const canShareFiles = (files = []) => {
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') {
    return false;
  }

  if (typeof navigator.canShare !== 'function') {
    return false;
  }

  const normalizedFiles = Array.isArray(files) ? files.filter(Boolean) : [];
  if (normalizedFiles.length === 0) {
    return false;
  }

  try {
    return navigator.canShare({ files: normalizedFiles });
  } catch (error) {
    return false;
  }
};

export const deliverFiles = async (files, options = {}) => {
  const {
    preferNativeShare = false,
    title,
    text,
    downloadDelayMs = DEFAULT_DOWNLOAD_DELAY_MS,
  } = options;

  const normalizedFiles = Array.isArray(files) ? files.filter(Boolean) : [];

  if (normalizedFiles.length === 0) {
    throw new Error('No files to deliver.');
  }

  if (preferNativeShare && canShareFiles(normalizedFiles)) {
    try {
      await navigator.share({
        files: normalizedFiles,
        ...(title ? { title } : {}),
        ...(text ? { text } : {}),
      });

      return { method: 'share' };
    } catch (error) {
      if (error?.name === 'AbortError') {
        return { method: 'cancelled' };
      }
    }
  }

  for (let index = 0; index < normalizedFiles.length; index += 1) {
    downloadFile(normalizedFiles[index]);

    if (index < normalizedFiles.length - 1) {
      await new Promise((resolve) => setTimeout(resolve, downloadDelayMs));
    }
  }

  return { method: 'download' };
};
